import { useState } from "react";
import { AlertTriangle, CheckCircle2, ShieldCheck } from "lucide-react";

import type {
  BankReconciliationStatement,
  ReconciliationBookItem,
  ReconciliationStatementItem,
} from "../../services/financialService";

const money = (value: string | null | undefined) => {
  const amount = Number(value ?? 0);
  return amount < 0 ? `(${Math.abs(amount).toLocaleString()})` : amount.toLocaleString();
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** 17 Sep 2026, day first as the school writes it, whatever the browser's language. */
const shortDate = (value: string | null | undefined) => {
  const [year, month, day] = (value ?? "").split("-").map(Number);
  return year && month && day ? `${day} ${MONTHS[month - 1]} ${year}` : "";
};

function Items({
  label,
  items,
  empty,
}: {
  label: string;
  items: Array<ReconciliationBookItem | ReconciliationStatementItem>;
  empty: string;
}) {
  return (
    <>
      <tr>
        <td className="px-4 pt-3 text-sm font-medium text-gray-700" colSpan={3}>{label}</td>
      </tr>
      {items.length === 0 ? (
        <tr>
          <td className="px-4 pb-2 pl-8 text-xs text-gray-400" colSpan={3}>{empty}</td>
        </tr>
      ) : (
        items.map((item) => {
          const manual = "manual" in item && item.manual;
          const reference = "entry_number" in item ? item.entry_number : item.reference;
          return (
            <tr key={"journal_entry" in item ? `e${item.journal_entry}` : `l${item.id}`} className="text-xs text-gray-600">
              <td className="whitespace-nowrap px-4 py-1 pl-8">
                {shortDate(item.date)}
                <span className={`ml-2 ${item.days_waiting > 30 ? "font-medium text-red-700" : "text-gray-400"}`}>
                  {item.days_waiting === 0 ? "today" : `${item.days_waiting} day${item.days_waiting === 1 ? "" : "s"}`}
                </span>
              </td>
              <td className="px-4 py-1">
                {item.description}
                {reference ? <span className="text-gray-400"> · {reference}</span> : null}
                {manual ? <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">typed in</span> : null}
              </td>
              <td className="whitespace-nowrap px-4 py-1 text-right tabular-nums">{money(item.amount)}</td>
            </tr>
          );
        })
      )}
    </>
  );
}

function Total({ label, amount, strong }: { label: string; amount: string; strong?: boolean }) {
  return (
    <tr className={strong ? "border-t border-gray-300" : ""}>
      <td className={`px-4 py-2 text-sm ${strong ? "font-semibold text-gray-900" : "text-gray-800"}`} colSpan={2}>{label}</td>
      <td className={`whitespace-nowrap px-4 py-2 text-right text-sm tabular-nums ${strong ? "font-semibold text-gray-900" : "text-gray-800"}`}>{money(amount)}</td>
    </tr>
  );
}

interface SignOffActions {
  /** The bursar puts this reconciliation up for review and signature. */
  onPrepare?: (notes: string) => void;
  /** The CFO takes back a signature, with a reason. */
  onWithdraw?: (id: number, reason: string) => void;
  busy?: boolean;
}

function SignOffPanel({ data, onPrepare, onWithdraw, busy }: { data: BankReconciliationStatement } & SignOffActions) {
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);
  const state = data.sign_off;
  const signed = state?.signed;
  const pending = state?.pending;
  const covered = signed && signed.as_at >= data.end_date;
  return (
    <div className="rounded-lg border border-gray-200 p-3 text-sm" aria-label="Sign-off">
      <div className="flex items-start gap-2">
        <ShieldCheck className={`mt-0.5 h-4 w-4 shrink-0 ${signed ? "text-green-700" : "text-gray-400"}`} />
        <div className="space-y-1">
          {signed ? (
            <p>
              Signed off to {shortDate(signed.as_at)} by {signed.signed_by ?? "the CFO"}
              {signed.prepared_by ? `, prepared by ${signed.prepared_by}` : ""}. Nothing dated on or before then can
              be entered for this bank.
            </p>
          ) : (
            <p className="text-gray-600">Not yet signed off.</p>
          )}
          {signed && !signed.still_holds && (
            <p className="font-medium text-red-700">
              Something dated inside the signed period has changed since it was signed. Ask the CFO to look at it.
            </p>
          )}
          {pending && (
            <p className="text-amber-700">
              The reconciliation to {shortDate(pending.as_at)} is waiting for{" "}
              {pending.workflow_status === "submitted" ? "review" : "the CFO's signature"} in My Approvals.
            </p>
          )}
        </div>
      </div>
      {onPrepare && !pending && !covered && data.is_reconciled && (
        <form
          className="mt-3 flex flex-wrap items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            onPrepare(notes.trim());
          }}
        >
          <input
            className="input min-w-64 flex-1 py-1 text-sm"
            aria-label="Notes for the reviewer"
            placeholder="Notes for the reviewer (optional)"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
          <button className="btn btn-primary py-1 text-sm" disabled={busy}>
            Put up for sign-off as at {shortDate(data.end_date)}
          </button>
        </form>
      )}
      {onWithdraw && signed && (
        withdrawing ? (
          <form
            className="mt-3 flex flex-wrap items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              onWithdraw(signed.id, reason.trim());
            }}
          >
            <input
              required
              className="input min-w-64 flex-1 py-1 text-sm"
              aria-label="Why withdraw the sign-off"
              placeholder="Why is the sign-off being withdrawn?"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            <button className="btn btn-secondary py-1 text-sm text-red-700" disabled={busy || !reason.trim()}>Withdraw sign-off</button>
            <button type="button" className="text-sm text-gray-500" onClick={() => setWithdrawing(false)}>Keep it</button>
          </form>
        ) : (
          <button type="button" className="mt-2 text-xs text-gray-500 underline" onClick={() => setWithdrawing(true)}>
            Withdraw this sign-off
          </button>
        )
      )}
    </div>
  );
}

/** A bank reconciliation as an accountant lays one out: the bank's balance
 *  and the books' balance, each adjusted for what the other has not seen yet. */
export default function BankReconciliationStatementView({
  data, ...actions
}: { data: BankReconciliationStatement } & SignOffActions) {
  const fromStatement = data.statement_balance_source === "statement";
  const linesDiffer = data.lines_differ_by !== null && Number(data.lines_differ_by) !== 0;
  return (
    <div className="mt-3 space-y-3">
      <SignOffPanel data={data} {...actions} />
      <div
        className={`flex items-start gap-2 rounded-lg p-3 text-sm ${data.is_reconciled ? "bg-green-50 text-green-800" : "bg-red-50 text-red-800"}`}
        role="status"
      >
        {data.is_reconciled ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
        <span>
          {data.is_reconciled
            ? `Reconciled as at ${shortDate(data.end_date)}: every difference between the bank and the books is listed below.`
            : `UGX ${money(data.difference)} is not explained as at ${shortDate(data.end_date)}. Match the lines below, or find what is missing.`}
        </span>
      </div>
      {!fromStatement && (
        <p className="text-xs text-amber-700">
          The statements imported show no balance of their own, so the bank balance here is the opening balance plus the lines
          imported. A line missing from the imports would not show.
        </p>
      )}
      {linesDiffer && (
        <p className="text-xs text-red-700">
          The bank shows UGX {money(data.statement_balance)}, but the opening balance and the lines imported come to UGX{" "}
          {money(data.lines_balance)}. A statement line is missing from the imports, or the opening balance was different.
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full text-left">
          <tbody>
            <Total
              label={`Balance per bank statement${data.statement_balance_date ? `, ${shortDate(data.statement_balance_date)}` : ""}`}
              amount={data.statement_balance}
            />
            <Items label="Add: received, not yet on the statement" items={data.deposits_not_on_statement} empty="Nothing waiting." />
            <Items label="Less: paid, not yet through the bank" items={data.payments_not_on_statement} empty="Nothing waiting." />
            <Total label="Adjusted bank balance" amount={data.adjusted_statement_balance} strong />
            <tr><td colSpan={3} className="py-2" /></tr>
            <Total label="Balance per books" amount={data.book_balance} />
            <Items label="Add: on the statement, not in the books" items={data.money_in_not_in_books} empty="Nothing to enter." />
            <Items label="Less: on the statement, not in the books" items={data.money_out_not_in_books} empty="Nothing to enter." />
            <Total label="Adjusted book balance" amount={data.adjusted_book_balance} strong />
            <Total label="Unexplained difference" amount={data.difference} strong />
          </tbody>
        </table>
      </div>
      <details className="text-sm">
        <summary className="cursor-pointer font-medium text-gray-700">
          Matched{data.start_date ? ` since ${shortDate(data.start_date)}` : ""} ({data.matched.length})
        </summary>
        <div className="mt-2 divide-y">
          {data.matched.map((item) => (
            <div key={item.id} className="flex justify-between gap-3 py-1 text-xs text-gray-600">
              <span>
                {shortDate(item.date)} · {item.description} · {item.entry_number}
                {item.reconciled_by ? <span className="text-gray-400"> · matched by {item.reconciled_by}</span> : null}
                {item.manual ? <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">typed in</span> : null}
              </span>
              <span className="tabular-nums">{money(item.amount)}</span>
            </div>
          ))}
          {!data.matched.length && <p className="py-1 text-xs text-gray-400">No lines matched in this period.</p>}
        </div>
      </details>
    </div>
  );
}
